import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  TableContainer,
  Text,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FiPlus, FiHome } from "react-icons/fi";
import VmsPageCard from "../components/VmsPageCard";
import { vmsApi } from "../services/vmsApi";
import { VMS_COLORS } from "../constants/theme";

const VmsOffices = () => {
  const [offices, setOffices] = useState([]);
  const [form, setForm] = useState({
    name: "",
    code: "",
    description: "",
    contact_email: "",
    requires_preapproval_for_pass: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const nextOfficeCode = useMemo(() => {
    const maxFromCodes = offices.reduce((max, office) => {
      const rawCode = String(office?.code || "");
      const match = rawCode.match(/(\d+)(?!.*\d)/);
      if (!match) return max;
      const serial = Number(match[1]);
      return Number.isFinite(serial) ? Math.max(max, serial) : max;
    }, 0);

    const nextSerial = maxFromCodes + 1;
    return `OFF-${String(nextSerial).padStart(3, "0")}`;
  }, [offices]);

  const loadOffices = async () => {
    try {
      const data = await vmsApi.getOffices();
      setOffices(Array.isArray(data) ? data : data?.data || []);
    } catch (err) {
      toast({ title: "Failed to load offices", description: err?.message, status: "error" });
    }
  };

  useEffect(() => {
    loadOffices();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await vmsApi.createOffice(form);
      setForm({
        name: "",
        code: "",
        description: "",
        contact_email: "",
        requires_preapproval_for_pass: true,
      });
      await loadOffices();
      toast({ title: "Office created", status: "success" });
      onClose();
    } catch (err) {
      toast({ title: "Create failed", description: err?.message, status: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenAddOffice = () => {
    setForm({
      name: "",
      code: nextOfficeCode,
      description: "",
      contact_email: "",
      requires_preapproval_for_pass: true,
    });
    onOpen();
  };

  return (
    <VStack align="stretch" spacing={5}>
      <VmsPageCard>
        <HStack justify="space-between" mb={4}>
          <Box>
            <Text fontSize="xl" fontWeight="800" color="gray.800">
              Offices
            </Text>
            <Text color="gray.500" fontSize="sm">
              Manage office records and locations.
            </Text>
          </Box>
          <Button
            leftIcon={<FiPlus />}
            onClick={handleOpenAddOffice}
            bg={VMS_COLORS.primary}
            color="white"
            borderRadius="12px"
            px={5}
            _hover={{ bg: VMS_COLORS.primaryDark, transform: "translateY(-1px)" }}
            _active={{ transform: "translateY(0)" }}
            boxShadow="0 10px 20px rgba(11,122,32,0.22)"
            transition="all 0.2s ease"
          >
            Add Office
          </Button>
        </HStack>

        <TableContainer border="1px solid" borderColor="gray.200" borderRadius="14px" overflow="hidden">
          <Table size="md" variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Office Name</Th>
                <Th>Code</Th>
                <Th>Contact Email</Th>
                <Th>Preapproval</Th>
                <Th>Description</Th>
              </Tr>
            </Thead>
            <Tbody>
              {offices.map((office) => (
                <Tr key={office.id || office.office_id || office.code} _hover={{ bg: "gray.50" }}>
                  <Td>
                    <HStack spacing={2}>
                      <Box as={FiHome} color={VMS_COLORS.primary} />
                      <Text fontWeight="600">{office.name || "-"}</Text>
                    </HStack>
                  </Td>
                  <Td>{office.code ? <Badge colorScheme="green">{office.code}</Badge> : "-"}</Td>
                  <Td color="gray.600">{office.contact_email || "-"}</Td>
                  <Td>
                    <Badge colorScheme={office.requires_preapproval_for_pass ? "purple" : "gray"}>
                      {office.requires_preapproval_for_pass ? "Required" : "Not Required"}
                    </Badge>
                  </Td>
                  <Td color="gray.600">{office.description || "-"}</Td>
                </Tr>
              ))}
              {!offices.length ? (
                <Tr>
                  <Td colSpan={6}>
                    <Text py={10} textAlign="center" color="gray.500">
                      No offices added yet. Click "Add Office" to create your first office.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </TableContainer>
      </VmsPageCard>

      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="16px">
          <ModalHeader fontSize="lg" fontWeight="700">
            Add Office
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <form id="create-office-form" onSubmit={handleCreate}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Name</FormLabel>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    borderRadius="10px"
                    h="46px"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Code</FormLabel>
                  <Input
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    borderRadius="10px"
                    h="46px"
                    bg="gray.50"
                    _focusVisible={{ bg: "white", borderColor: VMS_COLORS.primary }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Contact Email</FormLabel>
                  <Input
                    type="email"
                    value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    borderRadius="10px"
                    h="46px"
                    placeholder="office-gov@example.com"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Pass Preapproval</FormLabel>
                  <HStack
                    h="46px"
                    px={3}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="10px"
                    bg="gray.50"
                    justify="space-between"
                  >
                    <Text fontSize="sm" color="gray.700">
                      Requires preapproval
                    </Text>
                    <Button
                      size="xs"
                      borderRadius="999px"
                      variant={form.requires_preapproval_for_pass ? "solid" : "outline"}
                      colorScheme={form.requires_preapproval_for_pass ? "purple" : "gray"}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          requires_preapproval_for_pass: !prev.requires_preapproval_for_pass,
                        }))
                      }
                    >
                      {form.requires_preapproval_for_pass ? "ON" : "OFF"}
                    </Button>
                  </HStack>
                </FormControl>
                <FormControl gridColumn={{ base: "span 1", md: "span 2" }}>
                  <FormLabel>Description</FormLabel>
                  <Input
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    borderRadius="10px"
                    h="46px"
                  />
                </FormControl>
              </SimpleGrid>
            </form>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} borderRadius="10px">
              Cancel
            </Button>
            <Button
              type="submit"
              form="create-office-form"
              bg={VMS_COLORS.primary}
              color="white"
              _hover={{ bg: VMS_COLORS.primaryDark }}
              borderRadius="10px"
              isLoading={isLoading}
            >
              Save Office
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default VmsOffices;
